// The compliance audit export's reading of a period (agion/Agion#3391).
//
// The export reads every mission event and governance decision of the period
// a page at a time and keeps running counts, so its totals cover the whole
// period and every run of the period is in the file. It lists at most
// COMPLIANCE_EXPORT_LISTED_EVENTS event rows and COMPLIANCE_EXPORT_LISTED_DECISIONS
// denied or escalated decisions, so the page never holds an unbounded number
// of rows; when it lists fewer than the period holds, the file's completeness
// block says so with both counts. The browser counts the rows the server
// served and applies no rule of its own.
import type { ExecutionAuditEntry, MissionAuditEntry } from './audit.service'
import { exportRunCounts, type RunResultReading } from './run-result'

/** Rows asked for per read; the governance service serves at most 1000. */
export const COMPLIANCE_EXPORT_PAGE = 1000
/**
 * Event rows the file lists; every row of the period is still read and counted.
 * Above the largest organisation (18,866) and mission (17,423) measured on
 * 2026-10-21, so those files list every row.
 */
export const COMPLIANCE_EXPORT_LISTED_EVENTS = 50_000
/** Denied and escalated decisions the file lists; every decision is still counted. */
export const COMPLIANCE_EXPORT_LISTED_DECISIONS = 50_000

/**
 * Whether a structural row is a person's answer, the one human-oversight
 * event the platform records unambiguously (agion/Agion#2834). A hitl_request
 * row is a question or an output message; the row cannot say which.
 */
export function isHumanAnswer(e: Pick<MissionAuditEntry, 'eventType' | 'agentId'>): boolean {
  return e.eventType === 'hitl_response' && e.agentId === 'human'
}

/** The fields of an event row the file lists; the rest of the row is not kept. */
type ListedEvent = Pick<
  MissionAuditEntry,
  | 'createdAt' | 'eventType' | 'agentId' | 'nodeId' | 'hitlRequestId' | 'toolName' | 'modelName' | 'decision'
  | 'trustScore' | 'success' | 'tokensInput' | 'tokensOutput' | 'durationMs' | 'outputHash' | 'errorCategory'
>

function listedEvent(e: MissionAuditEntry): ListedEvent {
  return {
    createdAt: e.createdAt,
    eventType: e.eventType,
    agentId: e.agentId,
    nodeId: e.nodeId,
    hitlRequestId: e.hitlRequestId,
    toolName: e.toolName,
    modelName: e.modelName,
    decision: e.decision,
    trustScore: e.trustScore,
    success: e.success,
    tokensInput: e.tokensInput,
    tokensOutput: e.tokensOutput,
    durationMs: e.durationMs,
    outputHash: e.outputHash,
    errorCategory: e.errorCategory,
  }
}

/**
 * Distinct values in the order version 1.0 of the file listed them: by the
 * time of their earliest event, a tie by the order the rows were read. The
 * rows arrive newest first, so insertion order alone would reverse it.
 */
class FirstSeen<T> {
  private readonly seen = new Map<T, { at: number; seq: number }>()

  note(value: T, at: number, seq: number): void {
    const prior = this.seen.get(value)
    if (!prior || at < prior.at) this.seen.set(value, { at, seq })
  }

  values(): T[] {
    return [...this.seen.entries()].sort(([, a], [, b]) => a.at - b.at || a.seq - b.seq).map(([value]) => value)
  }
}

/** One run's running counts over all its events, and the events the file lists. */
interface RunTally {
  executionId: string
  startMs: number
  start?: string
  endMs: number
  end?: string
  hosts: FirstSeen<string | null>
  agents: FirstSeen<string>
  tools: FirstSeen<string>
  totalEvents: number
  agentTurns: number
  toolCalls: number
  humanAnswers: number
  humanRequests: number
  failures: number
  tokensInput: number
  tokensOutput: number
  durationMs: number
  answeredRequests: Set<string>
  listed: ListedEvent[]
}

function newRun(executionId: string): RunTally {
  return {
    executionId,
    startMs: Infinity,
    endMs: -Infinity,
    hosts: new FirstSeen(),
    agents: new FirstSeen(),
    tools: new FirstSeen(),
    totalEvents: 0,
    agentTurns: 0,
    toolCalls: 0,
    humanAnswers: 0,
    humanRequests: 0,
    failures: 0,
    tokensInput: 0,
    tokensOutput: 0,
    durationMs: 0,
    answeredRequests: new Set(),
    listed: [],
  }
}

/** Running counts over every mission event of the period, grouped by mission and run. */
export class ComplianceEventTally {
  readonly missions = new Map<string, Map<string, RunTally>>()
  read = 0
  listed = 0
  tokensInput = 0
  tokensOutput = 0
  readonly agents = new Set<string>()
  readonly hosts = new Set<string | null>()
  humanOversightEvents = 0
  humanRequestEvents = 0
  failedEvents = 0
  /** False when the list served a page that repeats the one before it and the reading stopped there. */
  readInFull = true

  add(e: MissionAuditEntry): void {
    let runs = this.missions.get(e.missionId)
    if (!runs) {
      runs = new Map()
      this.missions.set(e.missionId, runs)
    }
    let run = runs.get(e.executionId)
    if (!run) {
      run = newRun(e.executionId)
      runs.set(e.executionId, run)
    }
    const at = new Date(e.createdAt).getTime()
    if (at < run.startMs || run.start === undefined) { run.startMs = at; run.start = e.createdAt }
    if (at > run.endMs || run.end === undefined) { run.endMs = at; run.end = e.createdAt }
    run.hosts.note(e.hostId, at, this.read)
    run.agents.note(e.agentId, at, this.read)
    run.totalEvents++
    if (e.eventType === 'agent_turn') run.agentTurns++
    if (e.eventType === 'tool_call') {
      run.toolCalls++
      if (e.toolName) run.tools.note(e.toolName, at, this.read)
    }
    if (e.eventType === 'hitl_request') {
      run.humanRequests++
      this.humanRequestEvents++
    }
    if (isHumanAnswer(e)) {
      run.humanAnswers++
      this.humanOversightEvents++
      if (e.hitlRequestId) run.answeredRequests.add(e.hitlRequestId)
    }
    if (e.success === false) {
      run.failures++
      this.failedEvents++
    }
    run.tokensInput += e.tokensInput || 0
    run.tokensOutput += e.tokensOutput || 0
    run.durationMs += e.durationMs || 0

    this.read++
    this.tokensInput += e.tokensInput || 0
    this.tokensOutput += e.tokensOutput || 0
    this.agents.add(e.agentId)
    this.hosts.add(e.hostId)
    if (this.listed < COMPLIANCE_EXPORT_LISTED_EVENTS) {
      run.listed.push(listedEvent(e))
      this.listed++
    }
  }
}

/** One run of the file: its summary counts all its events; its lists hold the events the file lists. */
export function exportedExecution(run: RunTally, runResult: RunResultReading | undefined) {
  const events = [...run.listed].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
  return {
    executionId: run.executionId,
    result: runResult?.result ?? null,
    resultCodes: runResult?.codes ?? [],
    period: { start: run.start, end: run.end },
    hosts: run.hosts.values(),
    agents: run.agents.values(),
    summary: {
      totalEvents: run.totalEvents,
      agentTurns: run.agentTurns,
      toolCalls: run.toolCalls,
      toolsUsed: run.tools.values(),
      hitlEvents: run.humanAnswers,
      humanRequests: run.humanRequests,
      failures: run.failures,
      totalTokensInput: run.tokensInput,
      totalTokensOutput: run.tokensOutput,
      totalDurationMs: run.durationMs,
    },
    /** How many of the run's events the lists below hold; summary.totalEvents counts them all. */
    eventsListed: events.length,
    humanOversight: events.filter(isHumanAnswer).map((e) => ({
      timestamp: e.createdAt,
      type: e.eventType,
      nodeId: e.nodeId,
      agentId: e.agentId,
      decision: e.decision,
      hitlRequestId: e.hitlRequestId,
    })),
    // Questions and output messages share hitl_request: listed, not
    // counted as oversight (dataProvenance.humanOversight).
    humanRequests: events.filter((e) => e.eventType === 'hitl_request').map((e) => ({
      timestamp: e.createdAt,
      nodeId: e.nodeId,
      agentId: e.agentId,
      hitlRequestId: e.hitlRequestId,
      answered: !!e.hitlRequestId && run.answeredRequests.has(e.hitlRequestId),
    })),
    errors: events.filter((e) => e.success === false).map((e) => ({
      timestamp: e.createdAt,
      eventType: e.eventType,
      agentId: e.agentId,
      errorCategory: e.errorCategory,
      toolName: e.toolName,
    })),
    events: events.map((e) => ({
      timestamp: e.createdAt,
      eventType: e.eventType,
      agentId: e.agentId,
      toolName: e.toolName,
      modelName: e.modelName,
      decision: e.decision,
      trustScore: e.trustScore,
      success: e.success,
      tokensInput: e.tokensInput,
      tokensOutput: e.tokensOutput,
      durationMs: e.durationMs,
      outputHash: e.outputHash,
      errorCategory: e.errorCategory,
    })),
  }
}

export type ExportedExecution = ReturnType<typeof exportedExecution>

/** One mission of the file: its summary counts every run and event of the period. */
export function exportedMission(missionId: string, executions: ExportedExecution[]) {
  return {
    missionId,
    summary: {
      totalExecutions: executions.length,
      totalEvents: executions.reduce((sum, e) => sum + e.summary.totalEvents, 0),
      ...exportRunCounts(executions.map((e) => ({ result: e.result, failedEvents: e.summary.failures }))),
      executionsWithHITL: executions.filter((e) => e.summary.hitlEvents > 0).length,
    },
    executions,
  }
}

function listedDecision(d: ExecutionAuditEntry) {
  return {
    timestamp: d.createdAt,
    agentId: d.agentId,
    agentTrustTier: d.agentTrustTier,
    action: d.action,
    resource: d.resource,
    decision: d.decision,
    reason: d.decisionReason,
    appliedRule: d.appliedRule,
  }
}

/**
 * Running counts over every governance decision of the period. With a
 * mission, only that mission's decisions count: the decision list has no
 * mission filter, so the rows are read for the organisation and the rest are
 * passed over; a decision recorded without a mission is not the mission's.
 */
export class ComplianceDecisionTally {
  read = 0
  total = 0
  allowed = 0
  denied = 0
  escalated = 0
  rateLimited = 0
  notListed = 0
  /** False when the list served a page that repeats the one before it and the reading stopped there. */
  readInFull = true
  readonly deniedActions: ReturnType<typeof listedDecision>[] = []
  readonly escalatedActions: ReturnType<typeof listedDecision>[] = []

  private readonly missionId?: string

  /** missionId is compared without case: the same uuid may be written in either case. */
  constructor(missionId?: string) {
    this.missionId = missionId?.toLowerCase()
  }

  add(d: ExecutionAuditEntry): void {
    this.read++
    if (this.missionId && d.missionId?.toLowerCase() !== this.missionId) return
    this.total++
    if (d.decision === 'allowed' || d.decision === 'allow') this.allowed++
    if (d.decision === 'rate_limited') this.rateLimited++
    const denied = d.decision === 'blocked' || d.decision === 'deny'
    const escalated = d.decision === 'escalated' || d.decision === 'escalate'
    if (!denied && !escalated) return
    if (denied) this.denied++
    else this.escalated++
    if (this.deniedActions.length + this.escalatedActions.length >= COMPLIANCE_EXPORT_LISTED_DECISIONS) {
      this.notListed++
      return
    }
    ;(denied ? this.deniedActions : this.escalatedActions).push(listedDecision(d))
  }

  summary() {
    return {
      totalDecisions: this.total,
      allowed: this.allowed,
      denied: this.denied,
      escalated: this.escalated,
      rateLimited: this.rateLimited,
      deniedActions: this.deniedActions,
      escalatedActions: this.escalatedActions,
    }
  }
}

/**
 * What the file holds against what the period holds. eventsCountedByServer
 * appears only when the server's own count for the period differs from the
 * rows the export read (rows written or removed while it read, or a read the
 * server cut short).
 */
export function exportCompleteness(input: {
  events: ComplianceEventTally
  serverEventTotal: number
  executions: readonly ExportedExecution[]
  decisions: ComplianceDecisionTally
  runResultsReadInFull: boolean
}) {
  const { events, serverEventTotal, executions, decisions, runResultsReadInFull } = input
  const allListed = executions.filter((e) => e.eventsListed === e.summary.totalEvents).length
  const noneListed = executions.filter((e) => e.eventsListed === 0 && e.summary.totalEvents > 0).length
  const someListed = executions.length - allListed - noneListed
  const decisionsListed = decisions.deniedActions.length + decisions.escalatedActions.length
  const serverAgrees = serverEventTotal === events.read

  const statements: string[] = []
  if (events.listed < events.read) {
    statements.push(
      `Event rows are listed for the newest ${events.listed} of ${events.read} events of the period. ` +
      `Every total and every execution summary counts all ${events.read}; ` +
      `${someListed} executions list part of their events and ${noneListed} list none (see eventsListed per execution).`,
    )
  }
  if (!events.readInFull) {
    statements.push(serverAgrees
      ? "The event list served the same page twice, so the reading stopped there; the server's count was met."
      : 'The event list served the same page twice, so the reading stopped there.')
  }
  if (!serverAgrees) {
    statements.push(`The server counted ${serverEventTotal} events for the period and the export read ${events.read}.`)
  }
  if (decisions.notListed > 0) {
    statements.push(
      `Denied and escalated actions are listed for ${decisionsListed} of ${decisionsListed + decisions.notListed} such decisions; the governance totals count all of them.`,
    )
  }
  if (!decisions.readInFull) {
    statements.push('The governance decision list stopped serving new rows, so the governance totals may count fewer decisions than the period holds.')
  }
  if (!runResultsReadInFull) {
    statements.push('The run list could not be read in full, so executions without a server result are counted by their events.')
  }
  // A repeated event page is reported on its own (eventsReadInFull); whether
  // the event read missed rows is decided by the server's count, which a
  // reading cut short by a repeated page cannot match unless it read them all.
  const complete = events.listed === events.read && serverAgrees &&
    decisions.notListed === 0 && decisions.readInFull && runResultsReadInFull

  return {
    complete,
    statement: statements.length > 0
      ? statements.join(' ')
      : 'The file lists every event, execution and governance decision of the period and scope.',
    eventsInPeriod: events.read,
    eventsListed: events.listed,
    ...(serverAgrees ? {} : { eventsCountedByServer: serverEventTotal }),
    eventsReadInFull: events.readInFull,
    executionsInPeriod: executions.length,
    executionsWithAllEventsListed: allListed,
    executionsWithSomeEventsListed: someListed,
    executionsWithNoEventsListed: noneListed,
    governanceDecisionsInPeriod: decisions.total,
    governanceActionsListed: decisionsListed,
    governanceActionsNotListed: decisions.notListed,
    governanceDecisionsReadInFull: decisions.readInFull,
    runResultsReadInFull,
  }
}
